import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyTierBadge from './LoyaltyTierBadge'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

function LoyaltyLandingPageTierInformation() {
  const intl = useIntl()
  const [config, configFetching, configError] = useLoyaltyConfig()

  const bronzeTitle = useMemo(
    () =>
      intl.formatMessage({
        id: '7ZOmawUW8c2ONUgBxJcG',
        defaultMessage: 'Your journey starts here',
        description: 'Title for bronze tier loyalty information in the loyalty section',
      }),
    [intl],
  )

  const bronzeDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'yjev7narsaEcFR4WE6m-',
        defaultMessage: 'Start earning points and status credits from your very first booking.',
        description: 'Description for bronze tier loyalty benefits in the loyalty section',
      }),
    [intl],
  )

  const silverTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'MGqsVdgpmcpAstuuZZA8',
        defaultMessage: 'Step up the benefits',
        description: 'Title for silver tier loyalty information in the loyalty section',
      }),
    [intl],
  )

  const silverDescriptionWithLounge = useMemo(
    () =>
      intl.formatMessage({
        id: 'bv51dKtDYlvgtUCeHnFH',
        defaultMessage: 'Access added perks like lounge passes, tour credits, and discounted photoshoots.',
        description: 'Description for silver tier loyalty benefits when lounge passes are enabled',
      }),
    [intl],
  )

  const silverDescriptionWithoutLounge = useMemo(
    () =>
      intl.formatMessage({
        id: '8d_LLFD_kG1N5wJpGBZK',
        defaultMessage: 'Access added perks like tour credits, and discounted photoshoots.',
        description: 'Description for silver tier loyalty benefits when lounge passes are not enabled',
      }),
    [intl],
  )

  const goldTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'iV5GkCXa-IUVo24FvxuK',
        defaultMessage: 'A new gold standard',
        description: 'Title for gold tier loyalty information in the loyalty program section',
      }),
    [intl],
  )

  const goldDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'G6X_9W6Y2Ugm9RiWz4l5',
        defaultMessage: 'Enjoy elevated benefits including free room upgrades and chauffeur airport transfers.',
        description: 'Description for gold tier loyalty benefits in the loyalty section',
      }),
    [intl],
  )

  const platinumTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'Vwagt12gneTkzdLu3zoc',
        defaultMessage: 'The complete VIP experience',
        description: 'Title for platinum tier loyalty information in the loyalty section',
      }),
    [intl],
  )

  const platinumDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'p0XmmJojyOZbLAWlTpg-',
        defaultMessage: 'Indulge in unlimited chauffeur transfers, additional room upgrades for the ultimate escape.',
        description: 'Description for platinum tier loyalty benefits on the promotional landing page',
      }),
    [intl],
  )

  if (configFetching) return <LoadingIndicator />
  if (!config || configError) return null

  return (
    <Group direction="vertical" tabletDirection="horizontal" gap={48} tabletGap={36}>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="bronze" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {bronzeTitle}
          </Heading>
          <BodyText variant="medium">
            {bronzeDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="silver" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {silverTitle}
          </Heading>
          {config.regionEnabledBenefits.lounge_pass && (
            <BodyText variant="medium">
              {silverDescriptionWithLounge}
            </BodyText>
          )}
          {!config.regionEnabledBenefits.lounge_pass && (
            <BodyText variant="medium">
              {silverDescriptionWithoutLounge}
            </BodyText>
          )}
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="gold" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {goldTitle}
          </Heading>
          <BodyText variant="medium">
            {goldDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="platinum" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {platinumTitle}
          </Heading>
          <BodyText variant="medium">
            {platinumDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
    </Group>
  )
}

export default LoyaltyLandingPageTierInformation
